
import React, { useState, useEffect } from 'react';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d3/d3.css';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { buildD2Payload } from '../../../services/payloads/d2Payload';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Alert, // notices
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InputAdornment from '@mui/material/InputAdornment';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';
import { fetchApi } from "../../../services/api";
import { getEightDInfoId } from '../../../services/eightDInfoStore';
import { getCurrentUserEmail } from '../../../services/auth';

// Props now accept eightDInfoId/currentUserEmail/onSaved like D1
const D2 = ({
  expanded,
  onSetClick,
  handleCloseForm,
  onToggleAccordion,
  eightDInfoId,
  currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '',
  onSaved,
  isEnabled = false,
  teamMembers = [],
  d2Data,
  d2ResponsibleData,
  mode,
  readOnly = false,
}) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  // For view: header is clickable if isEnabled, but editing blocked by effectiveEnabled
  const canView = isEnabled;
  // States for Q1–Q4
  const [responsiblePerson, setResponsiblePerson] = useState('');
  // DatePickers should use null Date instead of '' strings
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);

  const [errors, setErrors] = useState({
    person: '',
    planned: '',
    achieved: '',
    q1: '',
    q2: '',
    q3Date: '',
    q3Location: '',
    q4: '',
  });

  const [problemComparison, setProblemComparison] = useState('');
  const [whyProblem, setwhyProblem] = useState('');
  const [detectionMethod, setDetectionMethod] = useState('');
  const [discoveryDate, setDiscoveryDate] = useState(null);
  const [defectiveDate, setDefectiveDate] = useState(null); // (unused in UI but kept if needed)
  const [discoveryLocation, setDiscoveryLocation] = useState('');
  const [discoveredBy, setDiscoveredBy] = useState('');
  const [affectedStatus, setAffectedStatus] = useState('Not Affected'); // 'Affected' or 'Not Affected'
  const [referenceText, setReferenceText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // NEW: Q5 comments (maps to defectivePartComment in API)
  const [defectivePartComment, setDefectivePartComment] = useState('');

  // Optional: expose a toggle if your API needs isDisabled
  const [isDisabled, setIsDisabled] = useState(false);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
  };
  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setUploadedFiles((prev) => [...prev, ...files]);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Q5: Dynamic rows for defective parts
  const [defectiveParts, setDefectiveParts] = useState([{ id: null, date: null, count: 0 }]);
  const [defectivePartsToRemove, setDefectivePartsToRemove] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const handleIncrease = (index) => {
    setDefectiveParts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, count: item.count + 1 } : item))
    );
    setHasChanges(true);
  };
  const handleDecrease = (index) => {
    setDefectiveParts((prev) =>
      prev.map((item, i) => (i === index && item.count > 0 ? { ...item, count: item.count - 1 } : item))
    );
    setHasChanges(true);
  };
  const handleDateChange = (index, date) => {
    setDefectiveParts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, date } : item))
    );
    setHasChanges(true);
    setSaveAttempted(false);
  };
  const handleAddRow = () => {
    setDefectiveParts((prev) => [...prev, { date: null, count: 0 }]);
    setHasChanges(true);
  };
  const handleRemoveRow = (index) => {
    const removed = defectiveParts[index];
    if (removed?.id) {
      setDefectivePartsToRemove(prev => [...prev, removed.id]);
    }
    setDefectiveParts((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  // Validation helpers
  const validateField = (field, value) => {
    let error = '';
    // if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    if (field === 'q1' && !value?.trim()) error = 'Please describe the problem';
    if (field === 'q2' && !value?.trim()) error = 'Please explain why it is a problem';
    if (field === 'q3Date' && !value) error = 'Discovery date is required';
    if (field === 'q3Location' && !value?.trim()) error = 'Discovery location is required';
    if (field === 'q4' && !value?.trim()) error = 'Detection method is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };
  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };
  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };
  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d2Id, setD2Id] = useState(null);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [formClosed, setFormClosed] = useState(true);
  const [saveAttempted, setSaveAttempted] = useState(false);
  // Achieved date in the form (only committed on Save)

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();
  // Block form when user is not originator (readOnly) and not the responsible person
  const isFormBlocked = readOnly ? !isCurrentUserResponsible : false;

  // Check if responsible person is already set (only when d2Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d2Id) {
      setIsResponsibleSet(true);
    }
  }, [d2Id]);


  // Load D2 data when available
  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d2Data && !d2ResponsibleData && mode === 'edit') {
      const fetchD2Data = async () => {
        try {
          const response = await fetchApi('/api/D2/GetD2', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          if (response?.data) {
            const d2 = response.data;
            if (d2.D2Id) { setD2Id(d2.D2Id); setFormSaved(true); }
            if (d2.ResponsiblePerson) setResponsiblePerson(d2.ResponsiblePerson);
            if (d2.PlannedDate) setPlannedDate(new Date(d2.PlannedDate));
            if (d2.AchievedDate) setAchievedDate(new Date(d2.AchievedDate));
            if (d2.WhatProblem) { setProblemComparison(d2.WhatProblem); setFormSaved(true); }
            if (d2.WhyProblem) setwhyProblem(d2.WhyProblem);
            if (d2.WhenProblemDiscovered) setDiscoveryDate(new Date(d2.WhenProblemDiscovered));
            if (d2.WhereProblemDiscovered) setDiscoveryLocation(d2.WhereProblemDiscovered);
            if (d2.ByWhomProblemDiscovered) setDiscoveredBy(d2.ByWhomProblemDiscovered);
            if (d2.HowProblemDetected) setDetectionMethod(d2.HowProblemDetected);
            if (d2.DefectivePartComment) setDefectivePartComment(d2.DefectivePartComment);
            setAffectedStatus(d2.AreOtherPartNumberAffected ? 'Affected' : 'Not Affected');
            if (d2.Reference) setReferenceText(d2.Reference);
            if (d2.DefectivePartsDetected && Array.isArray(d2.DefectivePartsDetected) && d2.DefectivePartsDetected.length > 0) {
              setDefectiveParts(d2.DefectivePartsDetected.map(p => ({
                id: p.D2DefectPartId || null,
                date: p.DetectedDate ? new Date(p.DetectedDate) : null,
                count: p.NoOfPartsDetected || 0
              })));
            }
            if (d2.Attachments && Array.isArray(d2.Attachments)) {
              setUploadedFiles(d2.Attachments);
            }
          }
        } catch (error) {
        }
      };
      fetchD2Data();
      return;
    }
    
    // Handle d2Data - can be object or array
    if (d2Data) {
      const data = Array.isArray(d2Data) ? d2Data[0] : d2Data;
      if (data) {
        if (data.D2Id) setD2Id(data.D2Id);
        setProblemComparison(data.WhatProblem || '');
        if (data.WhatProblem) setFormSaved(true);
        setwhyProblem(data.WhyProblem || '');
        setDiscoveryDate(data.WhenProblemDiscovered ? new Date(data.WhenProblemDiscovered) : null);
        setDiscoveryLocation(data.WhereProblemDiscovered || '');
        setDiscoveredBy(data.ByWhomProblemDiscovered || '');
        setDetectionMethod(data.HowProblemDetected || '');
        setDefectivePartComment(data.DefectivePartComment || '');
        setAffectedStatus(data.AreOtherPartNumberAffected ? 'Affected' : 'Not Affected');
        setReferenceText(data.Reference || '');
        
        if (data.DefectivePartsDetected && Array.isArray(data.DefectivePartsDetected) && data.DefectivePartsDetected.length > 0) {
          setDefectiveParts(data.DefectivePartsDetected.map(p => ({
            id: p.D2DefectPartId || null,
            date: p.DetectedDate ? new Date(p.DetectedDate) : null,
            count: p.NoOfPartsDetected || 0
          })));
        }
        if (data.Attachments && Array.isArray(data.Attachments)) {
          setUploadedFiles(data.Attachments);
        }
      }
    }
    
    // Handle d2ResponsibleData - can be object or array
    if (d2ResponsibleData) {
      const respData = Array.isArray(d2ResponsibleData) ? d2ResponsibleData[0] : d2ResponsibleData;
      if (respData) {
        if (respData.D2Id) setD2Id(respData.D2Id);
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
  }, [eightDInfoId, d2Data, d2ResponsibleData]);

  const isFormValid = () => {
    // trigger validations (defensive)
    validateField('person', responsiblePerson);
    validateField('planned', plannedDate);
    validateField('achieved', achievedDate);
    validateField('q1', problemComparison);
    validateField('q2', whyProblem);
    validateField('q3Date', discoveryDate);
    validateField('q3Location', discoveryLocation);
    validateField('q4', detectionMethod);

    return (
      responsiblePerson &&
      plannedDate &&
      achievedDate &&
      !errors.person &&
      !errors.planned &&
      !errors.achieved &&
      problemComparison?.trim() &&
      whyProblem?.trim() &&
      discoveryDate &&
      discoveryLocation?.trim() &&
      detectionMethod?.trim() &&
      !errors.q1 &&
      !errors.q2 &&
      !errors.q3Date &&
      !errors.q3Location &&
      !errors.q4 &&
      eightDInfoId &&
      currentUserEmail
    );
  };

  // Utilities
  const toIsoDateOrNull = (d) => (d instanceof Date ? d.toISOString() : null);



  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson || !plannedDate) {
      setSaveError('Please select both Responsible Person and Planned Date.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }
    
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        D2Id: d2Id || undefined,
        EightDInfoId: currentEightDInfoId,
        ResponsiblePerson: responsiblePerson,
        PlannedDate: plannedDate?.toISOString(),
        AchievedDate: achievedDate?.toISOString(),
        CreatedBy: currentUserEmail,
        ModifiedBy: currentUserEmail,
        CreatedOn: new Date().toISOString(),
        ModifiedOn: new Date().toISOString(),
        IsDisabled: true
      };

      const endpoint = d2Id ? '/api/D2/UpdateD2Responsible' : '/api/D2/CreateD2';
      
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D2Id) {
        setD2Id(response.data.D2Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d2Id ? "Updated D2 form successfully." : "Responsible person assigned successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person and planned date.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const hasInvalidDefectiveParts = defectiveParts.some((part, index) => 
    (part.count > 0 && !part.date)
  );

  const handleSave = async () => {
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }
    if (!currentUserEmail) {
      setSaveError('currentUserEmail is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    // Check for empty new rows (skip the first default row)
    const hasEmptyRows = defectiveParts.slice(1).some(part => !part.date && part.count === 0);
    
    if (hasEmptyRows) {
      setSaveAttempted(true);
      return;
    }

    if (hasInvalidDefectiveParts) {
      setSaveAttempted(true);
      return;
    }

    setSaving(true);
    try {
      const defectivePartsDetected = defectiveParts
        .filter(part => part.date && part.count > 0)
        .map(part => ({
          ...(part.id ? { D2DefectPartId: Number(part.id) } : {}),
          detectedDate: part.date,
          noOfPartsDetected: part.count
        }));

      // Validate files before processing
      const validFiles = uploadedFiles.filter(f => {
        if (f.isExisting) return true;
        if (!f.preview) {
          console.warn('File missing preview data:', f);
          return false;
        }
        return true;
      });
      
      if (uploadedFiles.length !== validFiles.length) {
        setSaveError('Some files are invalid. Please re-upload the files.');
        setTimeout(() => setSaveError(null), 3000);
        return; // Prevent API call with invalid files
      }

      let payload;
      try {
        payload = buildD2Payload({
          d2Id,
          eightDInfoId: currentEightDInfoId,
          whatProblem: problemComparison,
          whyProblem: whyProblem,
          whenProblemDiscovered: discoveryDate?.toISOString() || '',
          whereProblemDiscovered: discoveryLocation,
          byWhomProblemDiscovered: discoveredBy,
          howProblemDetected: detectionMethod,
          defectivePartComment: defectivePartComment,
          areOtherPartNumberAffected: affectedStatus === 'Affected',
          reference: referenceText,
          responsiblePerson,
          plannedDate,
          achievedDate,
          uploadedFiles: validFiles,
          defectivePartsDetected,
          isDisabled,
          attachmentsToRemove: attachmentsToRemove,
          defectivePartsDetectedtoRemove: defectivePartsToRemove,
          createdBy: currentUserEmail,
          modifiedBy: currentUserEmail,
        });
      } catch (payloadError) {
        setSaveError(`File validation error: ${payloadError.message}`);
        setTimeout(() => setSaveError(null), 3000);
        return;
      }

      const endpoint = d2Id ? '/api/D2/UpdateD2' : '/api/D2/CreateD2';
      
      // Log payload for debugging 400 errors
      console.log('[D2] API Call Debug:', {
        endpoint,
        payloadSize: JSON.stringify(payload).length,
        attachmentsCount: payload.Attachments?.length || 0,
        defectivePartsCount: payload.DefectivePartsDetected?.length || 0,
        eightDInfoId: payload.EightDInfoId,
        d2Id: payload.D2Id
      });
      
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D2Id || response?.data?.d2Id) {
        const newD2Id = response.data.D2Id || response.data.d2Id;
        setD2Id(newD2Id);
      }

      // Re-fetch D2 data to get assigned IDs for child records
      try {
        const refreshed = await fetchApi('/api/D2/GetD2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(currentEightDInfoId) }),
        });
        if (refreshed?.data) {
          const d2 = refreshed.data;
          if (d2.DefectivePartsDetected && Array.isArray(d2.DefectivePartsDetected) && d2.DefectivePartsDetected.length > 0) {
            setDefectiveParts(d2.DefectivePartsDetected.map(p => ({
              id: p.D2DefectPartId || null,
              date: p.DetectedDate ? new Date(p.DetectedDate) : null,
              count: p.NoOfPartsDetected || 0
            })));
          }
          if (d2.Attachments && Array.isArray(d2.Attachments)) {
            setUploadedFiles(d2.Attachments);
          }
        }
      } catch (e) {
        // Non-critical
      }

      setHasChanges(false);
      setFormSaved(true);
      setDefectivePartsToRemove([]);
      setAttachmentsToRemove([]);
      setSaveNotice("D2 details saved successfully.");
      setTimeout(() => setSaveNotice(null), 2000);
      onSaved && onSaved(response);
    } catch (e) {
      console.error('[D2] Save error details:', {
        message: e.message,
        stack: e.stack
      });
      
      let errorMessage = e?.message ?? 'Failed to save D2 details.';
      
      // Parse specific API errors
      if (errorMessage.includes('400')) {
        errorMessage = 'Invalid data submitted. Please check all fields and try again.';
      } else if (errorMessage.includes('401')) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (errorMessage.includes('403')) {
        errorMessage = 'Permission denied. You may not have access to modify this form.';
      } else if (errorMessage.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      setSaveError(errorMessage);
      setTimeout(() => setSaveError(null), 5000); // Show error longer for debugging
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    // Allow viewing when readOnly
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (isEnabled && isEditMode && d2Id) {
      onToggleAccordion();
    } else if (isEnabled && !d2Id) {
      setSaveError('Please click Set to assign responsible person first.');
      setTimeout(() => setSaveError(null), 3000);
    }
  };

  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        {/* Left Section */}
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D2</span>
          <span className="d3-subtitle">Describe the Problem.</span>

          {/* Tooltip */}
          <Tooltip
            title={
              <span>
                Provide details about the problem, assign responsibility, and set planned and achieved dates.
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Responsible Person Dropdown */}
        <Box className="responsible-box" onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled || isFormBlocked}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && (
            <Typography className="error-text">{errors.person}</Typography>
          )}
        </Box>

        {/* Planned Date */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(d) => handlePlannedChange(d)}
            label="Planned Date"
            disabled={!effectiveEnabled || isFormBlocked || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
            error={!!errors.planned}
            helperText={errors.planned || ''}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setHasChanges(false);
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setHasChanges(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
                setTimeout(() => { setHasChanges(false); }, 0);
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet || isFormBlocked}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); setHasChanges(false); }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {/* Accordion Form */}
      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} />
          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            <Box className="accordion-close-button-container" sx={{ mb: 2 }}>
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => { setIsEditMode(false); setFormClosed(true); handleCloseForm(); }}
              >
                Close Form
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, p: 3, backgroundColor: '#fff', borderRadius: '8px' }}>

              {/* Left Column for Q1–Q4 */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Q1 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q1</span>
                    <span className="d2-question-text">
                      What is the problem in comparison to a conforming part?
                    </span>
                  </Box>
                  <TextField
                    variant="standard"
                    placeholder="Write problem"
                    value={problemComparison}
                    onChange={(e) => {
                      setProblemComparison(e.target.value);
                      validateField('q1', e.target.value);
                      setHasChanges(true);
                    }}
                    className="d2-input"
                    multiline
                    sx={{
                      '& textarea': {
                        maxHeight: '120px',
                        overflowY: 'auto',
                        resize: 'none',
                      },
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                    error={!!errors.q1}
                    helperText={errors.q1 || ''}
                  />
                </Box>

                {/* Q2 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q2</span>
                    <span className="d2-question-text">
                      Why is it a problem?
                    </span>
                  </Box>
                  <TextField
                    variant="standard"
                    placeholder="Write problem"
                    value={whyProblem}
                    onChange={(e) => {
                      setwhyProblem(e.target.value);
                      validateField('q2', e.target.value);
                      setHasChanges(true);
                    }}
                    className="d2-input"
                    error={!!errors.q2}
                    helperText={errors.q2 || ''}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                </Box>

                {/* Q3 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q3</span>
                    <span className="d2-question-text">When was the problem discovered?</span>
                  </Box>
                  <Box className="q3-row">
                    <TwoMonthDatePicker
                      className="col-12 col-sm-6 col-md-4 float-left"
                      value={discoveryDate}
                      onChange={(date) => {
                        setDiscoveryDate(date);
                        validateField('q3Date', date);
                        setHasChanges(true);
                      }}
                      label="When?"
                      error={!!errors.q3Date}
                      helperText={errors.q3Date || ''}
                    />
                    <TextField
                      variant="standard"
                      placeholder="Where?"
                      value={discoveryLocation}
                      onChange={(e) => {
                        setDiscoveryLocation(e.target.value);
                        validateField('q3Location', e.target.value);
                        setHasChanges(true);
                      }}
                      className="d2-input"
                      error={!!errors.q3Location}
                      helperText={errors.q3Location || ''}
                      sx={{
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#800040',
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#ccc',
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottomColor: '#800040',
                        }
                      }}
                    />
                    <TextField
                      variant="standard"
                      placeholder="By whom?"
                      value={discoveredBy}
                      onChange={(e) => { setDiscoveredBy(e.target.value); setHasChanges(true); }}
                      className="d2-input"
                      sx={{
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#800040',
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#ccc',
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottomColor: '#800040',
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Q4 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q4</span>
                    <span className="d2-question-text">
                      How was it detected?
                    </span>
                  </Box>
                  <TextField
                    variant="standard"
                    placeholder="Write problem"
                    value={detectionMethod}
                    onChange={(e) => {
                      setDetectionMethod(e.target.value);
                      validateField('q4', e.target.value);
                      setHasChanges(true);
                    }}
                    className="d2-input"
                    error={!!errors.q4}
                    helperText={errors.q4 || ''}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Right Column for Q5–Q6 */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Q5 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q5</span>
                    <span className="d2-question-text">How many defective parts have been detected?</span>
                  </Box>

                  {defectiveParts.map((item, index) => {
                    // Check if this is a newly added row (index > 0) without a date
                    const isEmpty = !item.date && item.count === 0;
                    const isRowWithCountButNoDate = item.count > 0 && !item.date;
                    const showDateError = (saveAttempted && isEmpty) || (saveAttempted && isRowWithCountButNoDate);
                    
                    return (
                      <Box key={index} className="q5-container">
                        <TwoMonthDatePicker
                          value={item.date}
                          onChange={(date) => handleDateChange(index, date)}
                          label="Date"
                          className="q5-date"
                          error={showDateError}
                          sx={showDateError ? {
                            '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
                            '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
                            '& .MuiInputLabel-root': { color: '#d32f2f !important' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#d32f2f !important' },
                          } : {}}
                        />
                        <Typography className="q5-label">No. of defective</Typography>
                        <IconButton onClick={() => handleDecrease(index)} className="q5-btn">
                          <RemoveIcon />
                        </IconButton>
                        <Typography className="q5-count">{item.count}</Typography>
                        <IconButton onClick={() => handleIncrease(index)} className="q5-btn">
                          <AddIcon />
                        </IconButton>
                        {/* Only show remove button for rows other than the first one */}
                        {index > 0 && (
                          <IconButton onClick={() => handleRemoveRow(index)} className="q5-remove-btn">
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                  <IconButton onClick={handleAddRow} className="q5-add-btn">
                    <AddIcon />
                  </IconButton>

                  <Box className="q5-comments">
                    <TextField
                      variant="standard"
                      placeholder="Add Comments"
                      value={defectivePartComment}
                      onChange={(e) => { setDefectivePartComment(e.target.value); setHasChanges(true); }}
                      className="d2-input"
                      sx={{
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#800040',
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#ccc',
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottomColor: '#800040',
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Q6 */}
                <Box className="question-block">
                  <Box className="question-label">
                    <span className="d2-qnum">Q6</span>
                    <span className="d2-question-text">Are other part number affected?</span>
                  </Box>
                  <Box className="q6-options">
                    <Button
                      className={`q6-btn ${affectedStatus === 'Affected' ? 'active' : ''}`}
                      onClick={() => { setAffectedStatus('Affected'); setHasChanges(true); }}
                    >
                      Affected
                    </Button>
                    <Button
                      className={`q6-btn ${affectedStatus === 'Not Affected' ? 'active' : ''}`}
                      onClick={() => { setAffectedStatus('Not Affected'); setHasChanges(true); }}
                    >
                      Not Affected
                    </Button>
                  </Box>
                  <TextField
                    variant="standard"
                    placeholder="Reference?"
                    value={referenceText}
                    onChange={(e) => { setReferenceText(e.target.value); setHasChanges(true); }}
                    fullWidth
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Optional file drop area handlers (if you show a drop zone somewhere) */}
            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
              setHasChanges(true);
            }} folderPath="D2" existingFiles={uploadedFiles} maxFiles={3} />

            {/* Save Button */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#800040',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4
                }}
                onClick={handleSave}
                disabled={saving || !hasChanges || isFormBlocked}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D2;
